import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from "lodash";
import styles from './style/LegendScaleSlider.module.scss';
import { ClearOutlined, UndoOutlined } from '@ant-design/icons';
import { InputNumber, Slider, Tooltip, Typography } from 'antd';

import {
  mapRange,
} from '../../generic/legendUtils';

interface IMinMax {
  legendMinValue: number;
  legendMaxValue: number;
}

interface LegendScaleSliderProps {
  showLegendScale: boolean;
  startValue: number,
  endValue: number,
  setStartValue: (val: number) => void,
  setEndValue: (val: number) => void,
  legendMinMaxValues: IMinMax;
  initialLegendValue: IMinMax;
  onLegendStartEndChanged: (min: number, max: number) => void;
}

const LegendScaleSlider = ({
  showLegendScale,
  legendMinMaxValues,
  setStartValue,
  setEndValue,
  startValue,
  endValue,
  initialLegendValue,
  onLegendStartEndChanged
}: LegendScaleSliderProps) => {

  const {legendMinValue, legendMaxValue} = legendMinMaxValues;

  const [legendSlideValue, setLegendSlideValue] = useState<[number, number]>([
    0, 100,
  ]);

  const handleLegendSlideChange = (values: [number, number]) => {
    const startVal = mapRange(
      values[0],
      [0, 100],
      [legendMinValue, legendMaxValue]
    );

    const endVal = mapRange(
      values[1],
      [0, 100],
      [legendMinValue, legendMaxValue]
    );

    const [startValue, endValue] = values;
    if (!_.inRange(startValue, 0, 101) || !_.inRange(endValue, 0, 101)) {
      return;
    }
    if (!_.inRange(startValue, 0, endValue)) {
      return;
    }
    setLegendSlideValue([startValue, endValue]);
    setStartValue(startVal);
    setEndValue(endVal);
    onLegendStartEndChanged(startVal, endVal);
  };

  const handleLegendStartChange = useCallback((value: number | null) => {
    if(!value) return
    if (!_.inRange(value, legendMinValue, legendMaxValue + 1)) {
      if(value < legendMinValue){
        setStartValue(legendMinValue);
        onLegendStartEndChanged(legendMinValue, endValue);
      }
      else if(value > legendMaxValue){
        setStartValue(legendMaxValue)
        onLegendStartEndChanged(legendMaxValue, legendMaxValue);
      }
      else{
        setStartValue(value);
        onLegendStartEndChanged(value, endValue);
      }
    }
    else{
      setStartValue(value);
      onLegendStartEndChanged(value, endValue);
    }

    const sliderStartVal = mapRange(
      value,
      [legendMinValue, legendMaxValue],
      [0, 100]
    );

    setLegendSlideValue([sliderStartVal, legendSlideValue[1]]);
  }, [endValue]);

  const handleLegendEndChange = useCallback((value: number | null) => {
    if(!value) return
    if (!_.inRange(value, legendMinValue, legendMaxValue)) {
      if(value < legendMinValue){
        setEndValue(legendMinValue);
        onLegendStartEndChanged(legendMinValue, legendMinValue);
      }
      else if(value > legendMaxValue){
        setEndValue(legendMaxValue);
        onLegendStartEndChanged(startValue, legendMaxValue);
      }
      else{
        setEndValue(value);
        onLegendStartEndChanged(startValue, value);
      }
    }
    else{
      setEndValue(value);
      onLegendStartEndChanged(startValue, value);
    }

    const sliderEndVal = mapRange(
      value,
      [legendMinValue, legendMaxValue],
      [0, 100]
    );
    setLegendSlideValue([legendSlideValue[0], sliderEndVal]);
  }, [startValue, legendMinValue, legendMaxValue]);

  const handleClearLegend = useCallback(() => {
    onLegendStartEndChanged(initialLegendValue.legendMinValue, initialLegendValue.legendMaxValue);
    handleLegendStartChange(initialLegendValue.legendMinValue)
    handleLegendEndChange(initialLegendValue.legendMaxValue)
    setLegendSlideValue([0, 100]);
  }, [legendMinValue, legendMaxValue]);

  if (!showLegendScale) return null;
  return (
    <div className={styles.container}>
      <div className={styles.inputNumberContainer}>
        <Tooltip title="Reset Legend Interval">
          <UndoOutlined style={{ color: 'black' }} onClick={handleClearLegend} />
        </Tooltip>
        <div className={styles.inputWrapper}>
          <Typography style={{ marginRight: 5, width: 40 }}>
            Min
          </Typography>
          <InputNumber
            value={startValue}
            onChange={handleLegendStartChange}
            min={legendMinValue}
            max={legendMaxValue}
            step={1}
            style={{ width: 100 }}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Typography style={{ marginRight: 5, width: 40 }}>
            Max
          </Typography>
          <InputNumber
            value={endValue}
            onChange={handleLegendEndChange}
            min={legendMinValue}
            max={legendMaxValue}
            step={1}
            style={{ width: 100 }}
          />
        </div>
      </div>
      <div>
        <Slider
          tooltipVisible={false}
          range
          value={legendSlideValue}
          onChange={handleLegendSlideChange}
        />
      </div>
    </div>
  );
};

export default LegendScaleSlider;
